import React, { useCallback } from "react";
import { PlateType, EFootprint, ESlotHeight } from "../../../Model/PlateType";
import { PlateTypeCalibration } from "../../../Model/PlateType";
import "./GeneralTabView.css";
import SwitchButton from "../../Buttons/SwitchButton";
import { useTypedSelector } from "../../../redux/reducers/RootReducer";
import { getPlateAddresses } from "../../../redux/reducers/PlateAddressReducer";
import { getModulesByType } from "../../../redux/reducers/ModuleReducer";
import { EModuleType } from "../../../Model/EModuleType";
import { getModuleCapabilitiesById } from "../../../redux/reducers/ModuleCapabilitiesReducer";
import { EHotelType } from "../../../Model/Module";
import { brotliCompress } from "zlib";
import CommonDropDown from "../../Common/DropDown/CommonDropDown";

type Props = {
  plateType: PlateType;
  plateTypeCalibration: PlateTypeCalibration;
  setPlateTypeCalibration: (plateTypeCalibration: PlateTypeCalibration) => void;
  waypointNames: string[];
};

const GeneraltabView = ({
  plateType,
  plateTypeCalibration,
  setPlateTypeCalibration,
  waypointNames,
}: Props) => {
  const loadPortModule = useTypedSelector(
    (state) => getModulesByType(state)[EModuleType.LOADPORT][0]
  );
  const hotelTypes = useTypedSelector(
    (state) =>
      getModuleCapabilitiesById(state)[loadPortModule.identifier].loadPort
        ?.hotelTypes ?? ([] as EHotelType[])
  );
  const plateAddresses = useTypedSelector((state) => getPlateAddresses(state));

  const isCurrentPlateTypeExistInTheStorage = useCallback(() => {
    for (let i = 0; i < plateAddresses.length; i++) {
      const plateAddress = plateAddresses[i];
      if (
        plateAddress.usedByPlate != null &&
        plateAddress.usedByPlate.plateType.id === plateType.id
      ) {
        return true;
      }
    }
    return false;
  }, [plateAddresses, plateType]);

  const onHotelTypeChanged = (value: string) => {
    let hotelType = Number.parseInt(value) as EHotelType;
    let footprint = EFootprint.SBS;
    let slotHeight = plateTypeCalibration.slotHeight;

    switch (hotelType) {
      case EHotelType.SBS: {
        footprint = EFootprint.SBS;
        slotHeight = ESlotHeight.OneSlot;
        break;
      }
      case EHotelType.LCP: {
        footprint = EFootprint.SBS;
        slotHeight = ESlotHeight.HalfSlot;
        break;
      }
      case EHotelType.Linbro: {
        footprint = EFootprint.Linbro;
        slotHeight = ESlotHeight.OneSlot;
      }
      case EHotelType.Nextal: {
        footprint = EFootprint.Nextal;
        slotHeight = ESlotHeight.OneSlot;
      }
      case EHotelType.Terazaki: {
        footprint = EFootprint.Terazaki;
        slotHeight = ESlotHeight.OneSlot;
      }
    }

    setPlateTypeCalibration({
      ...plateTypeCalibration,
      footprint: footprint,
      slotHeight: slotHeight,
    });
  };

  const onHotelSlotHeightChanged = (value: string) => {
    const slotHeight = Number.parseInt(value) as ESlotHeight;
    setPlateTypeCalibration({
      ...plateTypeCalibration,
      slotHeight: slotHeight,
    });
  };

  let hotelType = EHotelType.SBS;

  switch (plateTypeCalibration.footprint) {
    case EFootprint.SBS: {
      hotelType =
        plateTypeCalibration.slotHeight === ESlotHeight.HalfSlot
          ? EHotelType.LCP
          : EHotelType.SBS;
      break;
    }
    case EFootprint.Linbro: {
      hotelType = EHotelType.Linbro;
      break;
    }
    case EFootprint.Nextal: {
      hotelType = EHotelType.Nextal;
      break;
    }
    case EFootprint.Terazaki: {
      hotelType = EHotelType.Terazaki;
      break;
    }
  }

  console.log("HOTELTYPE", { hotelType: EHotelType[hotelType] });
  let plateBumpVectorDisplayValues = waypointNames.map((wpName) => ({
    display: wpName,
    value: wpName,
    disabled: false,
  }));
  if (!waypointNames.includes(plateTypeCalibration.plateBumpWaypointName)) {
    plateBumpVectorDisplayValues.unshift({
      display: plateTypeCalibration.plateBumpWaypointName,
      value: plateTypeCalibration.plateBumpWaypointName,
      disabled: false,
    });
  }

  return (
    <div className="general-tab-container">
      <div
        style={{
          gridArea: "plate-layout-label",
        }}
      >
        Plate Layout
      </div>

      <div
        style={{
          gridArea: "hotel-type-label",
        }}
      >
        Hotel Type
      </div>

      <div
        style={{
          gridArea: "columns-label",
        }}
      >
        Columns
      </div>

      <div
        style={{
          gridArea: "drops-label",
        }}
      >
        Drops
      </div>

      <div
        style={{
          gridArea: "rows-label",
        }}
      >
        Rows
      </div>

      <div
        style={{
          gridArea: "slots-label",
        }}
      >
        Slots
      </div>

      <div
        style={{
          gridArea: "plate-alignment-label",
        }}
      >
        Plate Alignment
      </div>

      <div
        style={{
          gridArea: "plate-bump-on-return-label",
        }}
      >
        Enable Bump on Plate Return
      </div>

      <div
        style={{
          gridArea: "drop-position-saving-label",
        }}
      >
        Enable Drop Position Saving
      </div>

      <div
        style={{
          gridArea: "plate-sensor-label",
        }}
      >
        Enable Plate Sensor
      </div>

      <input
        style={{
          gridArea: "plate-layout-input",
        }}
        readOnly
        value="Standard"
      />

      <input
        style={{
          gridArea: "columns-input",
        }}
        readOnly
        value={plateType.numberOfColumn}
      />

      <input
        style={{
          gridArea: "drops-input",
        }}
        readOnly
        value={plateType.numberOfDropPerWell}
      />

      <input
        style={{
          gridArea: "rows-input",
        }}
        readOnly
        value={plateType.numberOfRow}
      />

      <CommonDropDown
        id="slots-input"
        style={{
          gridArea: "slots-input",
          width: "100%",
          height: 34,
        }}
        
        disabled={
          isCurrentPlateTypeExistInTheStorage() || hotelType === EHotelType.LCP
        }
        value={plateTypeCalibration.slotHeight}
        onChange={(val) => onHotelSlotHeightChanged(val.toString())}
        dropDownDisplayValues={[
          {
            display: "1",
            value: ESlotHeight.OneSlot,
            disabled: false,
          },
          {
            display: "2",
            value: ESlotHeight.TwoSlot,
            disabled: false,
          },
        ]}
      />

      <CommonDropDown
        id="hotel-type-select"
        style={{
          gridArea: "hotel-type-select",
          width: "100%",
          height: 34,
          backgroundColor: isCurrentPlateTypeExistInTheStorage()
            ? "#c4c4c4"
            : "#FFFFFF",
        }}
        disabled={isCurrentPlateTypeExistInTheStorage()}
        value={hotelType}
        onChange={(val) => onHotelTypeChanged(val.toString())}
        dropDownDisplayValues={hotelTypes.map((hotelType) => ({
          display: EHotelType[hotelType],
          value: hotelType,
          disabled: false,
        }))}
      />

      <SwitchButton
        style={{
          gridArea: "plate-bump-on-return-toggle",
          margin: "auto",
        }}
        isOn={plateTypeCalibration.plateBumpOnReturn}
        withLetter={false}
        onToggle={(isOn) =>
          setPlateTypeCalibration({
            ...plateTypeCalibration,
            plateBumpOnReturn: isOn,
          })
        }
      />

      {/* <select
        style={{
          gridArea: "plate-alignment-select",
        }}
        value={plateTypeCalibration.plateBumpWaypointName}
        onChange={(e) =>
          setPlateTypeCalibration({
            ...plateTypeCalibration,
            plateBumpWaypointName: e.target.value,
          })
        }
      >
        {!waypointNames.includes(
          plateTypeCalibration.plateBumpWaypointName
        ) && (
          <option
            key={"general-option " + plateTypeCalibration.plateBumpWaypointName}
            value={plateTypeCalibration.plateBumpWaypointName}
          >
            {plateTypeCalibration.plateBumpWaypointName}
          </option>
        )}
        {waypointNames.map((waypointName) => (
          <option key={"general-option " + waypointName} value={waypointName}>
            {waypointName}
          </option>
        ))}
      </select> */}

      <CommonDropDown
        id="plate-alignment-select"
        style={{
          gridArea: "plate-alignment-select",
          width: "100%",
          height: 34,
          color: "#000000",
        }}
        value={plateTypeCalibration.plateBumpWaypointName}
        onChange={(val) =>
          setPlateTypeCalibration({
            ...plateTypeCalibration,
            plateBumpWaypointName: val,
          })
        }
        dropDownDisplayValues={plateBumpVectorDisplayValues}
      />

      <SwitchButton
        style={{
          gridArea: "plate-bump-on-return-toggle",
          margin: "auto",
        }}
        isOn={plateTypeCalibration.plateBumpOnReturn}
        withLetter={false}
        onToggle={(isOn) =>
          setPlateTypeCalibration({
            ...plateTypeCalibration,
            plateBumpOnReturn: isOn,
          })
        }
      />

      <SwitchButton
        style={{
          gridArea: "drop-position-saving-toggle",
          margin: "auto",
        }}
        isOn={plateTypeCalibration.dropPositionSaving}
        withLetter={false}
        onToggle={(isOn) =>
          setPlateTypeCalibration({
            ...plateTypeCalibration,
            dropPositionSaving: isOn,
          })
        }
      />

      <SwitchButton
        style={{
          gridArea: "plate-sensor-toggle",
          margin: "auto",
        }}
        isOn={plateTypeCalibration.platePresenceSensor}
        withLetter={false}
        onToggle={(isOn) =>
          setPlateTypeCalibration({
            ...plateTypeCalibration,
            platePresenceSensor: isOn,
          })
        }
      />
    </div>
  );
};

export default GeneraltabView;
